import { ChevronDown, Copy06, Lightning01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { COLUMN_CLASSNAME_MAP } from 'src/app/Orders/order.const';
import BadgeCmp from 'src/components/BadgeCmp';
import CheckBox from 'src/components/CheckBox';
import TableCmp from 'src/components/TableCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateText from 'src/components/TruncateText';
import ActivityDropdownList, { IActivityDisplayItem } from 'src/components/V3DropdownCmp/ActivityDropdownList';
import CarrierDetailTooltip from 'src/components/V3DropdownCmp/CarrierDetailTooltip';
import { getBadgeLabelByOrderStatus, CLASSIFICATION_BADGE_CONFIG } from 'src/constants/common';
import { BadgeType } from 'src/interface/common';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { RootState } from 'src/redux/store';
import { logError } from 'src/services/LoggingService';
import { getQuoteActivities } from 'src/services/QuoteService';
import { mapActivitiesToDisplayItems } from 'src/utils/activityFeed.helpers';
import { copyToClipboard, formatAddress, getDateWithSuffixFormat, getFormattedDate, getFormattedDateWithTime, getFormattedNumber, usePhone } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

const LoadDetailRow = ({ data: dim, weightMeasurement, handlingUnitOptions }: { data: any; weightMeasurement: string; handlingUnitOptions?: any[] }) => {
    const handlingUnitLabel = handlingUnitOptions?.find((o: any) => o.value === Number(dim?.handlingUnit))?.label;

    return (
        <>
            <td className="">{handlingUnitLabel || dim?.handlingUnit || '-'}</td>
            <td className="">{getFormattedNumber(dim?.handlingUnitNo || 1, false)}</td>
            <td className="">
                {dim?.length && dim?.width && dim?.height ? `${getFormattedNumber(dim.length, false)}"x${getFormattedNumber(dim.width, false)}"x${getFormattedNumber(dim.height, false)}"` : '-'}
            </td>
            <td className="">{dim?.totalWeight ? `${getFormattedNumber(dim.totalWeight, false)} ${weightMeasurement}` : '-'}</td>
            <td className="">{dim?.freightClass || '-'}</td>
        </>
    );
};

interface NewOrderRowProps {
    data: any;
    navigate: any;
    timeFilter?: any;
    onRowClick?: (data: any) => () => void;
    headCells?: any[];
    selectedOrderIds?: number[];
    onToggleSelect?: (id: number) => void;
}

const NewOrderRow: React.FC<NewOrderRowProps> = ({ data, onRowClick, headCells, selectedOrderIds, onToggleSelect }) => {
    const { t } = useTranslation();
    const { getFormatPhoneWithPlus } = usePhone();
    const appearance = useSelector((state: RootState) => state.basic.appearance);
    const currentUser = useSelector(currentUserSelector);
    const orderIdRef = useRef<HTMLDivElement>(null);
    const salesRepNameRef = useRef<HTMLParagraphElement>(null);
    const carrierNameRef = useRef<HTMLParagraphElement>(null);
    const shipperFullAddressRef = useRef<HTMLParagraphElement>(null);
    const consigneeFullAddressRef = useRef<HTMLParagraphElement>(null);
    const [isOriginTooltipOpen, setIsOriginTooltipOpen] = useState(false);
    const [isDestinationTooltipOpen, setIsDestinationTooltipOpen] = useState(false);
    const [isCarrierTooltipOpen, setIsCarrierTooltipOpen] = useState(false);
    const [isLoadDetailsTooltipOpen, setIsLoadDetailsTooltipOpen] = useState(false);
    const [isLatestActivityTooltipOpen, setIsLatestActivityTooltipOpen] = useState(false);
    const [orderActivities, setOrderActivities] = useState<any[]>([]);
    const [isActivityLoading, setIsActivityLoading] = useState(false);
    const handlingUnitOptions = useSelector((state: RootState) => state.basic.handlingUnitOptions);

    // Same "Latest Activity" popover as QuoteCustomerRow.tsx, backed by the order's own activity feed
    // (the same fetchEntityActivities endpoint powering the order detail page, entityType: 'order').
    useEffect(() => {
        if (!isLatestActivityTooltipOpen) return;

        setIsActivityLoading(true);
        getQuoteActivities({ entityType: 'order', entityId: data.id })
            .then((response: any) => {
                setOrderActivities(response?.data?.activities || []);
            })
            .catch(logError)
            .finally(() => setIsActivityLoading(false));
    }, [isLatestActivityTooltipOpen]);

    const activityDisplayItems = useMemo<IActivityDisplayItem[]>(() => mapActivitiesToDisplayItems(orderActivities), [orderActivities]);

    const badgeType = useMemo((): BadgeType => {
        const status = data?.orderStatus;
        if (status === 'draft' || status === 'cancelled' || status === 'dispatched') return 'pink';
        if (status === 'booked' || status === 'delivered') return 'lightblue';
        if (status === 'in-transit' || status === 'booking_failed') return 'error';
        if (status === 'completed') return 'gray';

        return 'success';
    }, [data?.orderStatus]);

    const badgeText = useMemo(() => (data?.orderStatus ? getBadgeLabelByOrderStatus(data.orderStatus) : 'Opened'), [data?.orderStatus]);

    const shipperAddress = useMemo(() => {
        const addr = data?.originAddress;
        if (!addr) return null;
        const stateCode = addr.state || addr.stateCode;
        const short = [addr.city, stateCode].filter(Boolean).join(', ');
        const full = addr.fullAddress || [addr.city, stateCode, addr.postal].filter(Boolean).join(', ');
        // City/state ONLY — 1:1 with wal-frontend-v2's Orders listing (NewOrderRow renders
        // `shipperCity, shipperState` in the cell). The shipper NAME is deliberately NOT in the
        // cell; it is the tooltip header below. BE sorts this column on the same city/state
        // value so the column order matches what is displayed.
        const display = formatAddress(short);

        return display ? { display, full: formatAddress(full) } : null;
    }, [data?.originAddress]);

    const consigneeAddress = useMemo(() => {
        const addr = data?.destinationAddress;
        if (!addr) return null;
        const stateCode = addr.state || addr.stateCode;
        const short = [addr.city, stateCode].filter(Boolean).join(', ');
        const full = addr.fullAddress || [addr.city, stateCode, addr.postal].filter(Boolean).join(', ');
        // City/state ONLY — same display rule as Origin above (name lives in the tooltip).
        const display = formatAddress(short);

        return display ? { display, full: formatAddress(full) } : null;
    }, [data?.destinationAddress]);

    // Same fallback as the order detail page (OrderStatus + CarrierAssignment): when no carrier
    // is assigned — or carrier visibility is off for this customer — WAL is shown as the carrier.
    const carrierName = data?.carrierData?.name || t('Common.Western_Alliance');

    // Same normalization as the order detail page's Carrier & Pricing card (CarrierAssignment.tsx):
    // strip non-digits so the raw column value renders as a clean "MC #1235". Keep the two in sync.
    const carrierMcId = useMemo(() => {
        const raw = data?.carrierData?.mcId;

        return raw ? String(raw).replace(/\D/g, '') || null : null;
    }, [data?.carrierData?.mcId]);

    // Contact rows for the carrier popover.
    //
    // These are the SALES REP's details, not the carrier's — deliberately identical to what the
    // order detail page's Carrier & Pricing Details card shows (CarrierAssignment.tsx reads
    // currentUser.salesRep for the Contact Name / Number / Email rows). The customer's point of
    // contact for a shipment is their WAL rep, never the carrier directly.
    //
    // salesRep is user-level state, so no per-row API data is needed. Rows with no value are
    // dropped rather than shown as "-", keeping the popover compact.
    const carrierContactDetails = useMemo(() => {
        const salesRep = currentUser?.salesRep;

        return [
            { label: t('Common.Contact_Name'), value: salesRep?.salesrepname },
            {
                label: t('Common.Contact_Number'),
                value: salesRep?.phone ? getFormatPhoneWithPlus(salesRep.phone) : null,
                copyValue: salesRep?.phone || undefined,
            },
            { label: t('Common.Email_Address'), value: salesRep?.salesrepemail, copyValue: salesRep?.salesrepemail || undefined },
        ].filter((row) => Boolean(row.value));
    }, [currentUser?.salesRep, t, getFormatPhoneWithPlus]);

    const classificationBadge = useMemo(() => {
        const orderType = data?.orderType?.toLowerCase();
        const config = CLASSIFICATION_BADGE_CONFIG[orderType];

        if (!config) {
            return (
                <BadgeCmp style="modern" type="gray">
                    {orderType?.toUpperCase() || '-'}
                </BadgeCmp>
            );
        }

        return (
            <BadgeCmp style="modern" type={config.type}>
                {t(config.label)}
            </BadgeCmp>
        );
    }, [data?.orderType, t]);

    const handleRowClick = () => {
        if (onRowClick) onRowClick(data)();
    };

    // Map column id → JSX for dynamic column ordering (mirrors QuoteCustomerRow pattern)
    const tableCells = useMemo(
        () => [
            {
                key: 'orderId',
                htmlDynamic: (
                    <td className={`${COLUMN_CLASSNAME_MAP.orderId} table-cell-with-tooltip`} onClick={handleRowClick}>
                        <div className="flex overflow-hidden h-full w-full">
                            <div className="w-fit max-w-full h-full">
                                <div className={`self-center flex items-center h-full hover:bg-utilityGray200 px-2 ${isLatestActivityTooltipOpen ? 'bg-utilityGray100' : ''}`}>
                                    <TruncateText refName={orderIdRef} message={`#${data?.orderId || data?.id}`}>
                                        <div ref={orderIdRef} className="text-utilityBlack800 font-semibold truncate cursor-pointer" onClick={handleRowClick}>
                                            #{data?.orderId || data?.id}
                                        </div>
                                    </TruncateText>
                                    <div onClick={(e) => e.stopPropagation()} className="h-full">
                                        <TooltipCmp
                                            TooltipCustomClass="tooltipV3-content-wrap"
                                            isArrow={false}
                                            isTooltipInteractive
                                            parentClassName="tooltip-content-with-scroll-wrap w-[400px] space-y-2 text-left"
                                            openOnClick
                                            position="bottom"
                                            onOpenChange={setIsLatestActivityTooltipOpen}
                                            message={
                                                <ActivityDropdownList
                                                    items={activityDisplayItems}
                                                    isLoading={isActivityLoading}
                                                    listTitle="Latest Activity"
                                                    emptyStateDesc="There is no activity recorded for this order yet."
                                                />
                                            }
                                        >
                                            <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isLatestActivityTooltipOpen ? '!opacity-100' : ''}`}>
                                                <ChevronDown className="w-4 h-4 text-grayLight600" />
                                            </div>
                                        </TooltipCmp>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </td>
                ),
            },
            {
                key: 'salesRep',
                htmlDynamic: (
                    <td className="min-w-[150px] max-w-[150px] w-[150px]" onClick={handleRowClick}>
                        {data?.userData?.firstName ? (
                            <div className="flex items-center gap-x-2 overflow-hidden">
                                {/* Avatar hidden for now — name only. See task to bring this back. */}
                                <TruncateText refName={salesRepNameRef} message={`${data.userData.firstName || ''} ${data.userData.lastName || ''}`.trim()}>
                                    <p ref={salesRepNameRef} className="truncate">
                                        {`${data.userData.firstName || ''} ${data.userData.lastName || ''}`.trim()}
                                    </p>
                                </TruncateText>
                            </div>
                        ) : (
                            <span>-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'orderStatus',
                htmlDynamic: (
                    <td className="min-w-[125px] max-w-[125px] w-[125px]" onClick={handleRowClick}>
                        <BadgeCmp style="modern" type={badgeType}>
                            {badgeText}
                        </BadgeCmp>
                    </td>
                ),
            },
            {
                key: 'orderType',
                htmlDynamic: (
                    <td className="min-w-[100px] max-w-[100px] w-[100px]" onClick={handleRowClick}>
                        {classificationBadge}
                    </td>
                ),
            },
            {
                key: 'shipperFullAddress',
                htmlDynamic: (
                    <td className="max-w-px min-w-[165px] cursor-pointer table-cell-with-tooltip">
                        {shipperAddress ? (
                            <TooltipCmp
                                TooltipCustomClass="tooltipV3-content-wrap"
                                isArrow={false}
                                position="bottom"
                                openOnClick
                                isTooltipInteractive
                                parentClassName="tooltip-content-with-scroll-wrap min-w-[220px] text-left space-y-1"
                                onOpenChange={setIsOriginTooltipOpen}
                                message={
                                    <>
                                        {data?.originAddress?.companyName && <h6 className="text-grayLight900 text-xs leading-[1.5] font-medium truncate">{data.originAddress.companyName}</h6>}
                                        <div className="tooltip-content-with-scroll flex justify-between gap-x-2 min-w-0">
                                            <TruncateText refName={shipperFullAddressRef} message={shipperAddress.full || '-'}>
                                                <p ref={shipperFullAddressRef} className="text-xs leading-[1.5] font-medium text-gray500 line-clamp-2 break-words">
                                                    {shipperAddress.full}
                                                </p>
                                            </TruncateText>
                                            <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                                                <Copy06
                                                    className="w-4 h-4 min-w-4 text-primary cursor-pointer flex-none"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(shipperAddress.full || '');
                                                        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
                                                    }}
                                                />
                                            </TooltipCmp>
                                        </div>
                                    </>
                                }
                            >
                                <div className={`flex items-center gap-x-1 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isOriginTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                                    <span className="font-medium truncate">{shipperAddress.display}</span>
                                    <div className={`table-icon-wrap flex-none ${isOriginTooltipOpen ? '!opacity-100' : ''}`}>
                                        <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                                    </div>
                                </div>
                            </TooltipCmp>
                        ) : (
                            <span className="table-col-tooltip-space-common-xs">-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'consigneeFullAddress',
                htmlDynamic: (
                    <td className="max-w-px min-w-[165px] cursor-pointer table-cell-with-tooltip">
                        {consigneeAddress ? (
                            <TooltipCmp
                                TooltipCustomClass="tooltipV3-content-wrap"
                                isArrow={false}
                                position="bottom"
                                openOnClick
                                isTooltipInteractive
                                parentClassName="tooltip-content-with-scroll-wrap min-w-[220px] text-left space-y-1"
                                onOpenChange={setIsDestinationTooltipOpen}
                                message={
                                    <>
                                        {data?.destinationAddress?.companyName && (
                                            <h6 className="text-grayLight900 text-xs leading-[1.5] font-medium truncate">{data.destinationAddress.companyName}</h6>
                                        )}
                                        <div className="tooltip-content-with-scroll flex justify-between gap-x-2 min-w-0">
                                            <TruncateText refName={consigneeFullAddressRef} message={consigneeAddress.full || '-'}>
                                                <p ref={consigneeFullAddressRef} className="text-xs leading-[1.5] font-medium text-gray500 line-clamp-2 break-words">
                                                    {consigneeAddress.full}
                                                </p>
                                            </TruncateText>
                                            <TooltipCmp TooltipCustomClass="tooltipV3-content-wrap" message={t('Common.Copy')}>
                                                <Copy06
                                                    className="w-4 h-4 min-w-4 text-primary cursor-pointer flex-none"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyToClipboard(consigneeAddress.full || '');
                                                        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
                                                    }}
                                                />
                                            </TooltipCmp>
                                        </div>
                                    </>
                                }
                            >
                                <div className={`flex items-center gap-x-1 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isDestinationTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                                    <span className="font-medium truncate">{consigneeAddress.display}</span>
                                    <div className={`table-icon-wrap flex-none ${isDestinationTooltipOpen ? '!opacity-100' : ''}`}>
                                        <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                                    </div>
                                </div>
                            </TooltipCmp>
                        ) : (
                            <span className="table-col-tooltip-space-common-xs">-</span>
                        )}
                    </td>
                ),
            },
            {
                key: 'createdAt',
                htmlDynamic: (
                    <td className="min-w-[165px] w-[165px] max-w-[165px]" onClick={handleRowClick}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data?.createdAt ? getDateWithSuffixFormat(getFormattedDate(data.createdAt, `MMM Do , hh:mm A`, true, true, appearance)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'updatedAt',
                htmlDynamic: (
                    <td className="min-w-[165px] w-[165px] max-w-[165px]" onClick={handleRowClick}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data?.updatedAt ? getDateWithSuffixFormat(getFormattedDate(data.updatedAt, `MMM Do , hh:mm A`, true, true, appearance)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'handlingUnits',
                htmlDynamic: (() => {
                    const dims = Array.isArray(data?.orderDimensions) ? data.orderDimensions : [];
                    const totalLineItems = data?.dimensionCount ?? dims.length;
                    const totalLinearFt = data?.linearFootage ? `${data.linearFootage} Ft` : '-';
                    const totalWeightDisplay = data?.totalWeight ? data.totalWeight : '-';

                    const cellText = data?.handlingUnits ? String(data.handlingUnits) : '-';

                    return (
                        <td className={`min-w-[155px] w-[155px] max-w-[155px] ${dims?.length > 0 ? 'table-cell-with-tooltip' : ''}`} onClick={handleRowClick}>
                            {dims?.length > 0 ? (
                                <div className={`flex items-center gap-x-1 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isLoadDetailsTooltipOpen ? ' !bg-utilityGray200' : ''}`}>
                                    <p className="truncate min-w-0 cursor-pointer">{cellText}</p>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <TooltipCmp
                                            TooltipCustomClass="tooltipV3-content-wrap"
                                            isArrow={false}
                                            position="bottom"
                                            openOnClick
                                            isTooltipInteractive
                                            parentClassName="tooltip-content-with-scroll-wrap w-[480px] space-y-2 text-left"
                                            onOpenChange={setIsLoadDetailsTooltipOpen}
                                            message={
                                                <>
                                                    <p className="text-xs text-grayLight900 font-medium">{t('Common.Line_Items')}</p>
                                                    <div className="tooltip-content-with-scroll  pb-1">
                                                        <div className="table-box-wrap">
                                                            <div className="table-box-container overflow-hidden">
                                                                <div className="overflow-x-auto custom-scrollbar scrollbar-hide table-without-title-header table-without-pagination">
                                                                    <TableCmp
                                                                        headCells={[
                                                                            { id: 'handlingUnitName', name: t('Common.Handling_Unit'), visible: true },
                                                                            { id: 'handlingUnitNo', name: '№', visible: true },
                                                                            { id: 'dimensions', name: t('Common.Dimensions'), visible: true },
                                                                            { id: 'freightClass', name: 'FC', visible: true },
                                                                            { id: 'totalWeight', name: t('Common.Total_Weight'), visible: true, headCellsTextClass: 'justify-end' },
                                                                        ]}
                                                                        tableDataArr={dims}
                                                                        TableRowCmp={LoadDetailRow}
                                                                        tableRowCmpProps={{
                                                                            weightMeasurement: (data?.weightMeasurement || 'lbs').toLowerCase(),
                                                                            handlingUnitOptions,
                                                                        }}
                                                                        tableRowHeight="xs"
                                                                        footerDataArr={[{}]}
                                                                        tableBodyRowClass="last:!border-utilityGray200"
                                                                        tableFooterRowClass="!border-b-0"
                                                                        TableFooterRowCmp={() => (
                                                                            <td colSpan={5} className="!p-0">
                                                                                <TableCmp
                                                                                    tableRowHeight="xs"
                                                                                    tableDataArr={[{}]}
                                                                                    tableBodyRowClass="divide-x divide-utilityGray200"
                                                                                    TableRowCmp={() => (
                                                                                        <>
                                                                                            <td className="w-[33.33%] text-grayLight900 font-medium ">
                                                                                                <div className="h-full flex items-center gap-x-2 justify-between">
                                                                                                    <p>{t('Common.Line_Item')}</p>
                                                                                                    <p> {totalLineItems}</p>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="w-[33.33%] text-grayLight900 font-medium ">
                                                                                                <div className="h-full flex items-center gap-x-2 justify-between">
                                                                                                    <p>{t('Common.Total_Linear_Ft')}</p>
                                                                                                    <p>{totalLinearFt}</p>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td className="w-[33.33%] text-grayLight900 font-medium ">
                                                                                                <div className="h-full flex items-center gap-x-2 justify-between">
                                                                                                    <p>{t('Common.Total_Weight')}</p>
                                                                                                    <p>{totalWeightDisplay}</p>
                                                                                                </div>
                                                                                            </td>
                                                                                        </>
                                                                                    )}
                                                                                />
                                                                            </td>
                                                                        )}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            }
                                        >
                                            <div className={`table-icon-wrap flex-none cursor-pointer ${isLoadDetailsTooltipOpen ? '!opacity-100' : ' '}`}>
                                                <ChevronDown className="w-4 h-4 text-grayLight600 flex-none " />
                                            </div>
                                        </TooltipCmp>
                                    </div>
                                </div>
                            ) : (
                                <p className="truncate min-w-0" onClick={handleRowClick}>
                                    {cellText}
                                </p>
                            )}
                        </td>
                    );
                })(),
            },
            {
                key: 'totalWeight',
                htmlDynamic: (
                    <td className="min-w-[140px] w-[140px] max-w-[140px]" onClick={handleRowClick}>
                        <span>
                            {data?.totalWeight
                                ? (() => {
                                      const match = String(data.totalWeight).match(/^([\d.]+)\s*(.*)$/);

                                      return match ? `${getFormattedNumber(parseFloat(match[1]), true)} ${match[2]}`.trim() : data.totalWeight;
                                  })()
                                : '-'}
                        </span>
                    </td>
                ),
            },
            {
                key: 'totalCost',
                htmlDynamic: (
                    <td className="min-w-[135px] w-[135px] max-w-[135px]" onClick={handleRowClick}>
                        <span>
                            ${getFormattedNumber(parseFloat(data?.totalCost || '0'), true, false)} {data.currency?.toUpperCase() || 'USD'}
                        </span>
                    </td>
                ),
            },
            {
                key: 'pickupDate',
                htmlDynamic: (
                    <td className="min-w-[155px] w-[155px] max-w-[155px]" onClick={handleRowClick}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data?.pickupDate ? getDateWithSuffixFormat(getFormattedDate(data.pickupDate, `MMM Do , YYYY`, false, false, appearance)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'deliveryDate',
                htmlDynamic: (
                    <td className="min-w-[155px] w-[155px] max-w-[155px]" onClick={handleRowClick}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: data?.deliveryDate ? getDateWithSuffixFormat(getFormattedDate(data.deliveryDate, `MMM Do , YYYY`, false, false, appearance)) : '-',
                            }}
                        />
                    </td>
                ),
            },
            {
                key: 'carrier',
                htmlDynamic: (
                    <td className="min-w-[190px] max-w-[190px] cursor-pointer table-cell-with-tooltip">
                        <TooltipCmp
                            TooltipCustomClass="tooltipV3-content-wrap"
                            isArrow={false}
                            position="bottom"
                            openOnClick
                            isTooltipInteractive
                            parentClassName="tooltip-content-with-scroll-wrap w-[400px] text-left"
                            onOpenChange={setIsCarrierTooltipOpen}
                            message={
                                <CarrierDetailTooltip imageUrl={data?.carrierData?.image} name={carrierName} contactLine={carrierMcId ? `MC #${carrierMcId}` : null} details={carrierContactDetails} />
                            }
                        >
                            <div className={`flex items-center gap-x-1 h-full table-cell-clickable table-col-tooltip-space-common-xs ${isCarrierTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                                {/* Carrier logo stays hidden in the cell (deliberate — name only); the logo is
                                    shown inside the popover, matching the detail page's carrier card. */}
                                <TruncateText refName={carrierNameRef} message={carrierName}>
                                    <p ref={carrierNameRef} className="font-medium truncate">
                                        {carrierName}
                                    </p>
                                </TruncateText>
                                <div className={`table-icon-wrap flex-none ${isCarrierTooltipOpen ? '!opacity-100' : ''}`}>
                                    <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                                </div>
                            </div>
                        </TooltipCmp>
                    </td>
                ),
            },
            {
                key: 'bolNumber',
                htmlDynamic: (
                    <td className="min-w-[145px] w-[145px] max-w-[145px]" onClick={handleRowClick}>
                        <span className="truncate">{data?.bolNumber || '-'}</span>
                    </td>
                ),
            },
            {
                key: 'refNumber',
                htmlDynamic: (
                    <td className="min-w-[145px] w-[145px] max-w-[145px]" onClick={handleRowClick}>
                        <span className="truncate">{data?.refNumber || '-'}</span>
                    </td>
                ),
            },
            {
                key: 'linearFootage',
                htmlDynamic: (
                    <td className="min-w-[130px] w-[130px] max-w-[130px]" onClick={handleRowClick}>
                        <span>{data?.linearFootage ? `${data.linearFootage} Ft` : '-'}</span>
                    </td>
                ),
            },
            {
                key: 'equipmentType',
                htmlDynamic: (
                    <td className="min-w-[145px] w-[145px] max-w-[145px]" onClick={handleRowClick}>
                        <span>{data?.equipmentTypeFullName || data?.equipmentType || '-'}</span>
                    </td>
                ),
            },
            {
                key: 'action',
                htmlDynamic: (
                    <td className="min-w-[38px] max-w-[38px] w-[38px] table-cell-with-tooltip sticky right-0 bg-inherit z-[1]" onClick={(e) => e.stopPropagation()}>
                        <div className="h-full flex items-center table-col-tooltip-space-common-xs">
                            {data?.isPriority && (
                                <TooltipCmp
                                    message={
                                        <div className="space-y-1 text-left">
                                            <p className="text-gray500 text-xs leading-[1.5] font-medium">Marked as priority by</p>
                                            <div className="space-y-0.5">
                                                {data?.prioritySetByName && <h6 className="text-grayLight900 text-sm font-medium">{data?.prioritySetByName}</h6>}
                                                {data?.prioritySetAt && (
                                                    <p className="text-grayLight600 text-xs leading-[1.5] font-normal">
                                                        {getFormattedDateWithTime(data?.prioritySetAt, 'ddd, MMM Do, YYYY [at] hh:mm A', true) || '-'}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    }
                                >
                                    <Lightning01 className="w-3.5 h-3.5 text-gray400 fill-gray400 flex-none" />
                                </TooltipCmp>
                            )}
                        </div>
                    </td>
                ),
            },
        ],
        [
            data,
            appearance,
            badgeType,
            badgeText,
            classificationBadge,
            shipperAddress,
            consigneeAddress,
            carrierName,
            carrierMcId,
            carrierContactDetails,
            isOriginTooltipOpen,
            isDestinationTooltipOpen,
            isCarrierTooltipOpen,
            isLoadDetailsTooltipOpen,
            isLatestActivityTooltipOpen,
            activityDisplayItems,
            isActivityLoading,
            handlingUnitOptions,
        ]
    );

    // Render cells in headCells order, respecting visibility — falls back to hardcoded order if headCells not provided
    if (!headCells) {
        return (
            <>
                {tableCells
                    .filter((c) => c.key !== 'action')
                    .map((c) => (
                        <React.Fragment key={c.key}>{c.htmlDynamic}</React.Fragment>
                    ))}
            </>
        );
    }

    return (
        <>
            {headCells
                .filter((cell: any) => cell.visible)
                .map((cell: any) => {
                    if (cell.id === 'checkbox') {
                        return (
                            <React.Fragment key="checkbox">
                                <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip" onClick={(e) => e.stopPropagation()}>
                                    <CheckBox
                                        id={`select-order-${data.id}`}
                                        checked={!!selectedOrderIds?.includes(data.id)}
                                        onChangeFunc={(e: any) => {
                                            e.stopPropagation();
                                            onToggleSelect?.(data.id);
                                        }}
                                        name="selectOrder"
                                        value={String(data.id)}
                                        parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                                    />
                                </td>
                            </React.Fragment>
                        );
                    }

                    const matched = tableCells.find((tc) => tc.key === cell.id);
                    if (!matched) return null;

                    return <React.Fragment key={cell.id}>{matched.htmlDynamic}</React.Fragment>;
                })}
        </>
    );
};

export default NewOrderRow;
